import React, { useCallback, forwardRef } from 'react'
import { CheckCircle2, AlertTriangle, XCircle, Info, ExternalLink, X } from 'lucide-react'
import { Button } from '@diversioteam/diversio-ds'

import type { NotificationItemProps } from './NotificationItem.types'
import * as S from './NotificationItem.styles'

import {
    SECONDS_IN_MINUTE,
    SECONDS_IN_HOUR,
    SECONDS_IN_DAY,
    SECONDS_IN_WEEK,
    ICON_SIZE_MEDIUM,
    ICON_SIZE_SMALL,
    ANIMATION_DURATION_FAST,
    ANIMATION_STAGGER_DELAY,
} from '@/api/actions/notifications/notificationConstants'

const TYPE_ICONS = {
    success: CheckCircle2,
    warning: AlertTriangle,
    failure: XCircle,
    info: Info,
} as const

// Helper function to format timestamp
function formatTimestamp(timestamp: string): string {
    const now = new Date()
    const date = new Date(timestamp)
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < SECONDS_IN_MINUTE) {
        return 'Just now'
    } else if (diffInSeconds < SECONDS_IN_HOUR) {
        const minutes = Math.floor(diffInSeconds / SECONDS_IN_MINUTE)
        return `${minutes}m ago`
    } else if (diffInSeconds < SECONDS_IN_DAY) {
        const hours = Math.floor(diffInSeconds / SECONDS_IN_HOUR)
        return `${hours}h ago`
    } else if (diffInSeconds < SECONDS_IN_WEEK) {
        const days = Math.floor(diffInSeconds / SECONDS_IN_DAY)
        return `${days}d ago`
    } else {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        })
    }
}

export const NotificationItem = forwardRef<HTMLDivElement, NotificationItemProps>(function NotificationItem(
    {
        notification,
        onClick,
        onDismiss,
        isFirst = false,
        isLast = false,
        animationIndex = 0,
        isDismissing = false,
        isNew = false,
    },
    ref,
) {
    const notificationType = notification.notification_type || 'info'
    const TypeIcon = TYPE_ICONS[notificationType as keyof typeof TYPE_ICONS] || Info

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            // Prevent click when clicking on action buttons
            if ((event.target as HTMLElement).closest('button')) {
                return
            }

            onClick(notification)
        },
        [notification, onClick],
    )

    const handleDismiss = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation()
            onDismiss(notification.id)
        },
        [notification.id, onDismiss],
    )

    const handleActionClick = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation()
            // Action button should navigate to the same place as clicking the notification card
            // This ensures consistent user experience - both interactions lead to the same destination
            onClick(notification)
        },
        [notification, onClick],
    )

    const formattedTimestamp = formatTimestamp(notification.created_at)
    const hasAction = notification.has_action_button && notification.button_label
    const userCountText = notification.user_count > 1 ? `${notification.user_count} people` : null

    return (
        <S.NotificationItemContainer
            ref={ref}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: ANIMATION_DURATION_FAST / 1000 }}
            $isFirst={isFirst}
            $isLast={isLast}
            $isDismissing={isDismissing}
            $type={notificationType}
            $isNew={isNew}
            onClick={handleClick}
            style={{
                // Stagger animation delay
                animationDelay: `${animationIndex * ANIMATION_STAGGER_DELAY}ms`,
                overflow: 'hidden', // Ensure content doesn't spill during height animation
            }}
        >
            <S.ClickableArea>
                <S.NotificationContent>
                    <S.NotificationTypeIcon $type={notificationType}>
                        <TypeIcon size={ICON_SIZE_MEDIUM} />
                    </S.NotificationTypeIcon>

                    <S.NotificationTextContent>
                        <S.NotificationTitleRow>
                            {isNew && <S.UnreadIndicator aria-hidden />}
                            <S.NotificationTitle>{notification.title}</S.NotificationTitle>
                            <S.NotificationTimestamp>{formattedTimestamp}</S.NotificationTimestamp>
                        </S.NotificationTitleRow>

                        {notification.description && (
                            <S.NotificationDescription>{notification.description}</S.NotificationDescription>
                        )}

                        {userCountText && (
                            <S.NotificationMeta>
                                <S.NotificationUserCount>{userCountText}</S.NotificationUserCount>
                            </S.NotificationMeta>
                        )}

                        <S.NotificationActions>
                            {hasAction && (
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={handleActionClick}
                                    rightIcon={<ExternalLink size={ICON_SIZE_SMALL} />}
                                >
                                    {notification.button_label}
                                </Button>
                            )}

                            <S.ActionButton
                                $variant="dismiss"
                                onClick={handleDismiss}
                                title={notification.dismiss_button_label || 'Dismiss'}
                            >
                                <X size={ICON_SIZE_SMALL} />
                                Dismiss
                            </S.ActionButton>
                        </S.NotificationActions>
                    </S.NotificationTextContent>
                </S.NotificationContent>
            </S.ClickableArea>
        </S.NotificationItemContainer>
    )
})

// Export component with display name for debugging
NotificationItem.displayName = 'NotificationItem'
